var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import a from "axios";
import { onGetSubOrdersFailure, onGetSubOrdersSuccess, } from "../../OrderFlow/components/SubscriptionSummaryPage/SubscriptionSummaryPage-utils";
import { ERROR_MESSAGE_GENERIC } from "../../Shared/utils/constants";
import { axiosPrivate } from "../../api/axios";
import { getCompanyCoords } from "./requests";
export const buildErrorMessage = (error, altGenericErrorMessage) => {
    if (a.isAxiosError(error))
        return error.message;
    else if (altGenericErrorMessage)
        return altGenericErrorMessage;
    else
        return ERROR_MESSAGE_GENERIC;
};
/**
 * This function is used to filter orders attached to the primary address. It is used to determine
 * if updating the delivery address will require remapping. If the order is past the cutoff, then
 * it will not be included in the filtered results because it cannot be modified.
 * @param primaryAddressId
 * @returns
 */
const primaryAddressFilterFunction = (primaryAddressId) => (o) => o.orderInfo.address_id === primaryAddressId.toString() && !o.order.IsOrderPastCutOff;
/**
 * This function is used to filter orders attached to the subscription linkId. It is used to determine
 * if updating the delivery address will require remapping. If the order is past the cutoff, then
 * it will not be included in the filtered results because it cannot be modified.
 * @param subscriptionLinkId
 * @returns
 */
const activeSubscriptionFilterFunction = (subscriptionLinkId) => (o) => o.order.SubscriptionLinkId === subscriptionLinkId && !o.order.IsOrderPastCutOff;
const buildAddressOrderData = (orders, primaryAddressId, subscriptionLinkId) => {
    if (!orders || (!primaryAddressId && !subscriptionLinkId))
        return null;
    const filterFunc = subscriptionLinkId
        ? activeSubscriptionFilterFunction(subscriptionLinkId)
        : primaryAddressFilterFunction(primaryAddressId ? primaryAddressId : -1);
    const filteredOrders = orders.filter(filterFunc).map((o) => ({
        deliverySchedule: o.deliverySchedule.deliveryScheduleId,
        orderId: o.order.OrderId,
        originalZip: o.order.Zip,
        primaryAddressId: primaryAddressId ? primaryAddressId : undefined,
        subscriptionLinkId: subscriptionLinkId ? subscriptionLinkId : undefined,
        programId: o.order.ProgramId,
        programSKU: o.order.ProgramSku,
    }));
    return filteredOrders ? filteredOrders : null;
};
export const doesPlaceChangeRequireReschedule = (orderId, locationSku, addressId) => __awaiter(void 0, void 0, void 0, function* () {
    const response = yield axiosPrivate.get("/Account/DoesPlaceChangeRequireReschedule", {
        params: {
            orderId: orderId,
            locationSku: locationSku,
            addressId: addressId,
        },
    });
    return {
        success: response.data.success,
        isAbleToBeModified: response.data.isAbleToBeModified,
        isPlaceUpdated: response.data.isPlaceUpdated,
        isRescheduleRequired: response.data.isRescheduleRequired,
        message: response.data.message,
    };
});
export const getRemappingInfo = (orderId, setOrderData) => {
    axiosPrivate
        .get("GetOrderInfo", {
        params: {
            orderId,
        },
    })
        .then((res) => {
        if (res && res.data) {
            setOrderData((prev) => (Object.assign(Object.assign({}, prev), { orderInfo: res.data.orderInfo, blockingCopy: res.data.blockingCopy, loading: false })));
        }
        else {
            setOrderData((prev) => (Object.assign(Object.assign({}, prev), { error: ERROR_MESSAGE_GENERIC, loading: false })));
        }
    })
        .catch((error) => {
        const errorMessage = buildErrorMessage(error);
        setOrderData((prev) => (Object.assign(Object.assign({}, prev), { error: errorMessage, loading: false })));
    });
};
/**
 * @param newZipCode
 * @param programSKU
 * @param locationSKU
 * @param onSuccess
 * @param onError
 */
export const getDeliveryScheduleID = (newZipCode, programSKU, order, locationSKU) => __awaiter(void 0, void 0, void 0, function* () {
    const res = yield axiosPrivate.get(`/Account/GetDeliverySchedule`, {
        params: {
            zipcode: newZipCode,
            programSKU: programSKU,
            locationSKU: locationSKU,
        },
    });
    if (res.data.deliverySchedule !== order.deliverySchedule)
        return true;
    else
        return false;
});
/**
 * Update Address Modal: filter pickup locations by programId.
 * @param {*} locations All locations.
 * @param {*} programId Order program ID for which the locations are to be displayed.
 * @returns
 */
export const filterLocationsByProgramId = (locations, programId) => programId ? locations.filter((loc) => { var _a; return (_a = loc === null || loc === void 0 ? void 0 : loc.locationProgramIds) === null || _a === void 0 ? void 0 : _a.includes(programId); }) : locations;
/**
 * Gets data for update address modal.
 * @param {*} setState Save fetch results to state.
 * @param {*} programId Order program ID for the addresses.
 */
export const getAddressesAndQuestions = (setState, programId, programSKU, orderId, initDeliveryAddressState, setCustomerShippingAddresses, subscriptionLinkId) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    try {
        const [deliveryAddresses, addressQuestions, companyAddressData, customerCreditCards] = yield Promise.all([
            axiosPrivate.get("/Account/GetAccountDeliveryAddresses", {
                params: {
                    programSKU: programSKU,
                    programId: programId,
                },
            }),
            axiosPrivate.get("/Account/GetAccountDeliveryAddressQuestions"),
            axiosPrivate.get("/Account/GetCompanyAddress"),
            axiosPrivate.get("/Account/GetCustomerPaymentMethods"),
        ]);
        const allAddresses = deliveryAddresses.data.data;
        const geoLocation = yield getCompanyCoords(companyAddressData.data.companyAddress);
        const programOnlyLocations = filterLocationsByProgramId(deliveryAddresses.data.pickupLocations, programId);
        // Looks for the primary address in the list of all addresses and returns the addressId.
        const primaryAddressId = (_a = allAddresses.find((a) => a.isPrimary)) === null || _a === void 0 ? void 0 : _a.addressId;
        const primaryAddressOrdersData = [];
        const subscriptionActiveOrdersData = [];
        // TODO: Querying orders for the primary address is required to determine if remapping is possible.
        // TODO: Currently the GetOrders query is overkill for the data needed. If the remapping logic remains on the FE, consider removing OrderInfo from the GetOrders return.
        // // If there is a primary address but no subscription linkId, then we need to get the orders for the primary address.
        // if (primaryAddressId && !subscriptionLinkId) {
        //   const ordersRes = await axiosPrivate.get(`/Account/GetOrders`, {
        //     params: {
        //       fetchAllOrders: true,
        //       pageIndex: null,
        //       pageSize: null,
        //     },
        //   });
        //   const primaryAddressOrders = buildAddressOrderData(ordersRes.data.orders, primaryAddressId, undefined);
        //   if (primaryAddressOrders) primaryAddressOrdersData.push(...primaryAddressOrders);
        // }
        // // If there is a subscription linkId, then we need to get the orders for the subscription linkId.
        // if (!!subscriptionLinkId) {
        //   const ordersRes = await axiosPrivate.get(`/Account/GetOrders`, {
        //     params: {
        //       fetchAllOrders: true,
        //       pageIndex: null,
        //       pageSize: null,
        //     },
        //   });
        //   const subscriptionActiveOrders = buildAddressOrderData(ordersRes.data.orders, undefined, subscriptionLinkId);
        //   if (subscriptionActiveOrders) subscriptionActiveOrdersData.push(...subscriptionActiveOrders);
        // }
        // * Update checkout context with all addresses.
        setCustomerShippingAddresses(allAddresses);
        setState((prev) => (Object.assign(Object.assign({}, prev), { addresses: allAddresses, pickupLocations: programOnlyLocations, loading: false, loadingAddresses: false, loadingError: false, deliveryQuestions: addressQuestions.data, geoLocationCoords: geoLocation.data.features[0].center, customerCreditCards: customerCreditCards.data.data, usesLocation: deliveryAddresses.data.usesLocation, usesZipCodes: deliveryAddresses.data.usesZipCodes, orderId: orderId ? orderId : undefined, primaryAddressId: primaryAddressId ? primaryAddressId : null, primaryAddressOrdersData: primaryAddressOrdersData, subscriptionActiveOrdersData: subscriptionActiveOrdersData, error: undefined })));
    }
    catch (error) {
        const errorMessage = buildErrorMessage(error);
        setState(Object.assign(Object.assign({}, initDeliveryAddressState), { loading: false, loadingAddresses: false, error: errorMessage }));
    }
});
export const getSubscriptionOrdersLists = (subscriptionLinkId, setSubscriptionOrdersState, store, toast, datadogLog, closeEditModal) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const res = yield axiosPrivate.get(`/Account/GetSubscriptionOrders?subscriptionLinkId=${subscriptionLinkId}`);
        onGetSubOrdersSuccess(setSubscriptionOrdersState, store, res);
    }
    catch (error) {
        console.error("Error fetching subscription orders:", error);
        onGetSubOrdersFailure(setSubscriptionOrdersState, toast, datadogLog, error, closeEditModal);
        throw error;
    }
});
